import { cn } from '@/lib/utils';
import { useDeployStore } from '@/store';
import { useWalletStore } from '@/store/walletStore';
import type { ethers } from 'ethers';
import {
    AlertTriangle,
    Box,
    Check,
    ChevronDown,
    ChevronRight,
    Copy,
    Eye,
    FileCode,
    Loader2,
    Send,
    Wallet,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

interface AbiFunction {
  name: string;
  type: 'function' | 'event' | 'constructor' | 'fallback' | 'receive';
  stateMutability?: 'pure' | 'view' | 'nonpayable' | 'payable';
  inputs: AbiInput[];
  outputs?: AbiOutput[];
}

interface AbiInput {
  name: string;
  type: string;
  internalType?: string;
}

interface AbiOutput {
  name: string;
  type: string;
}

interface CallResult {
  functionName: string;
  result?: unknown;
  error?: string;
  timestamp: Date;
}

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

function getMutabilityColor(mutability?: string): string {
  switch (mutability) {
    case 'pure':
      return 'text-purple-400';
    case 'view':
      return 'text-blue-400';
    case 'payable':
      return 'text-yellow-400';
    default:
      return 'text-orange-400';
  }
}

function getMutabilityLabel(mutability?: string): string {
  switch (mutability) {
    case 'pure':
      return 'pure';
    case 'view':
      return 'view';
    case 'payable':
      return 'payable';
    default:
      return 'write';
  }
}

function isReadFunction(fn: AbiFunction): boolean {
  return fn.stateMutability === 'view' || fn.stateMutability === 'pure';
}

/* ================================================================== */
/*  Function Card                                                     */
/* ================================================================== */

interface FunctionCardProps {
  fn: AbiFunction;
  contractAddress: string;
  rpcUrl: string;
  abi: AbiFunction[];
  walletConnected: boolean;
}

function FunctionCard({ fn, contractAddress, rpcUrl, abi, walletConnected }: FunctionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [ethValue, setEthValue] = useState('');
  const [result, setResult] = useState<CallResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  /** Parse user input to typed args based on ABI types */
  function parseArgs(fnInputs: AbiInput[]): unknown[] {
    return fnInputs.map((input) => {
      const val = inputs[input.name] || '';
      if (input.type.startsWith('uint') || input.type.startsWith('int')) return val;
      if (input.type === 'bool') return val === 'true';
      if (input.type.includes('[]')) {
        try {
          return JSON.parse(val);
        } catch {
          return [];
        }
      }
      return val;
    });
  }

  const handleCall = useCallback(async () => {
    setIsLoading(true);
    try {
      const isRead = isReadFunction(fn);
      const { ethers } = await import('ethers');

      if (isRead) {
        // Read calls → use JSON-RPC provider (no wallet needed)
        const provider = rpcUrl
          ? new ethers.JsonRpcProvider(rpcUrl)
          : new ethers.BrowserProvider(
              (window as unknown as { ethereum: unknown }).ethereum as ethers.Eip1193Provider,
            );
        const contract = new ethers.Contract(contractAddress, abi as ethers.InterfaceAbi, provider);
        const args = parseArgs(fn.inputs);
        const res = await contract[fn.name](...args);
        setResult({
          functionName: fn.name,
          result: typeof res === 'bigint' ? res.toString() : res,
          timestamp: new Date(),
        });
      } else if (walletConnected) {
        // Write calls → use wallet signer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ethereum = (window as any).ethereum;
        if (!ethereum) throw new Error('No wallet provider found');

        const provider = new ethers.BrowserProvider(ethereum);
        const signer = await provider.getSigner();
        const contract = new ethers.Contract(contractAddress, abi as ethers.InterfaceAbi, signer);
        const args = parseArgs(fn.inputs);
        const overrides: Record<string, unknown> = {};
        if (fn.stateMutability === 'payable' && ethValue.trim()) {
          overrides.value = ethers.parseEther(ethValue);
        }
        const tx = await contract[fn.name](...args, overrides);
        const receipt = await tx.wait();
        setResult({
          functionName: fn.name,
          result: {
            hash: receipt.hash,
            blockNumber: receipt.blockNumber,
            gasUsed: receipt.gasUsed?.toString(),
            status: receipt.status === 1 ? 'success' : 'reverted',
          },
          timestamp: new Date(),
        });
      } else {
        setResult({
          functionName: fn.name,
          error:
            'Connect a wallet to call write functions. Open the Wallet panel from the activity bar.',
          timestamp: new Date(),
        });
      }
    } catch (err: unknown) {
      setResult({
        functionName: fn.name,
        error: err instanceof Error ? err.message : String(err),
        timestamp: new Date(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [fn, inputs, ethValue, contractAddress, rpcUrl, abi, walletConnected]);

  const isRead = isReadFunction(fn);

  return (
    <div className="bg-[#252526] border border-[#333] rounded overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[#2a2a2a] transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3 h-3 text-[#666] shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-[#666] shrink-0" />
        )}
        {isRead ? (
          <Eye className="w-3 h-3 text-blue-400 shrink-0" />
        ) : (
          <Send className="w-3 h-3 text-orange-400 shrink-0" />
        )}
        <span className="text-xs text-[#d4d4d4] font-mono flex-1 truncate">{fn.name}</span>
        <span
          className={cn('text-[9px] px-1 py-px rounded', getMutabilityColor(fn.stateMutability))}
        >
          {getMutabilityLabel(fn.stateMutability)}
        </span>
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-2 border-t border-[#333]">
          {/* Inputs */}
          {fn.inputs.length > 0 && (
            <div className="space-y-1.5 pt-2">
              {fn.inputs.map((input) => (
                <div key={input.name}>
                  <label className="text-[9px] text-[#666]">
                    {input.name} <span className="text-purple-400">({input.type})</span>
                  </label>
                  <input
                    type="text"
                    value={inputs[input.name] ?? ''}
                    onChange={(e) =>
                      setInputs((prev) => ({ ...prev, [input.name]: e.target.value }))
                    }
                    placeholder={input.type}
                    className="w-full bg-[#1e1e1e] text-xs text-[#d4d4d4] rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 border border-[#444] font-mono mt-0.5"
                  />
                </div>
              ))}
            </div>
          )}

          {/* ETH value for payable */}
          {fn.stateMutability === 'payable' && (
            <div>
              <label className="text-[9px] text-[#666]">ETH Value</label>
              <input
                type="text"
                value={ethValue}
                onChange={(e) => setEthValue(e.target.value)}
                placeholder="0.0"
                className="w-full bg-[#1e1e1e] text-xs text-[#d4d4d4] rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 border border-[#444] font-mono mt-0.5"
              />
            </div>
          )}

          {/* Call button */}
          <button
            onClick={handleCall}
            disabled={isLoading}
            className={cn(
              'w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium transition-colors disabled:opacity-50',
              isRead
                ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 border border-blue-600/30'
                : 'bg-orange-600/20 text-orange-400 hover:bg-orange-600/30 border border-orange-600/30',
            )}
          >
            {isLoading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : isRead ? (
              <Eye className="w-3 h-3" />
            ) : (
              <Send className="w-3 h-3" />
            )}
            {isRead ? 'Read' : 'Write'}
          </button>

          {/* Result */}
          {result && (
            <div
              className={cn(
                'rounded p-2 text-[10px]',
                result.error
                  ? 'bg-red-500/10 border border-red-500/20'
                  : 'bg-green-500/10 border border-green-500/20',
              )}
            >
              {result.error ? (
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 text-red-400 shrink-0 mt-0.5" />
                  <span className="text-red-400">{result.error}</span>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-1">
                    <span className="text-[#666]">Result:</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(result.result));
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="p-0.5 text-[#666] hover:text-white transition-colors"
                    >
                      {copied ? (
                        <Check className="w-2.5 h-2.5 text-green-400" />
                      ) : (
                        <Copy className="w-2.5 h-2.5" />
                      )}
                    </button>
                  </div>
                  <pre className="mt-1 text-green-300 font-mono bg-[#1e1e1e] p-1.5 rounded text-[9px] overflow-auto max-h-24">
                    {typeof result.result === 'object'
                      ? JSON.stringify(result.result, null, 2)
                      : String(result.result)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Outputs */}
          {fn.outputs && fn.outputs.length > 0 && (
            <div className="text-[9px] text-[#555]">
              Returns: {fn.outputs.map((o) => `${o.name || '_'}: ${o.type}`).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Main Component                                                    */
/* ================================================================== */

export default function ContractInteractionPanel() {
  const { compileResult, deployResult } = useDeployStore();
  const {
    isConnected: walletConnected,
    address: walletAddress,
    connect: connectWallet,
  } = useWalletStore();
  const [filterType, setFilterType] = useState<'all' | 'read' | 'write'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const contractAddress = deployResult?.contractAddress;
  const abi: AbiFunction[] = useMemo(
    () => ((compileResult?.abi ?? []) as AbiFunction[]).filter((item) => item.type === 'function'),
    [compileResult],
  );

  const filteredFunctions = useMemo(() => {
    let fns = abi;
    if (filterType === 'read') fns = fns.filter(isReadFunction);
    else if (filterType === 'write') fns = fns.filter((fn) => !isReadFunction(fn));
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      fns = fns.filter((fn) => fn.name.toLowerCase().includes(q));
    }
    return fns;
  }, [abi, filterType, searchQuery]);

  if (!contractAddress || !compileResult?.abi) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-[#555] text-xs p-6 text-center">
        <Box className="w-8 h-8 mb-2 text-[#444]" />
        <p>No deployed contract</p>
        <p className="text-[10px] mt-1">
          Compile and deploy a contract first, then interact with its functions here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Contract info */}
      <div className="bg-[#1e1e1e] rounded p-2 space-y-1">
        <div className="flex items-center gap-1.5 text-[10px]">
          <FileCode className="w-3 h-3 text-green-400" />
          <span className="text-[#666]">Contract:</span>
          <span className="text-green-300 font-mono truncate">{contractAddress}</span>
        </div>
        {compileResult.contractName && (
          <div className="text-[10px] text-[#555]">
            {compileResult.contractName} — {abi.length} functions
          </div>
        )}
      </div>

      {/* Wallet status */}
      <div
        className={cn(
          'rounded p-2 text-[10px] flex items-center gap-2',
          walletConnected
            ? 'bg-green-500/10 border border-green-500/20'
            : 'bg-yellow-500/10 border border-yellow-500/20',
        )}
      >
        <Wallet
          className={cn('w-3.5 h-3.5', walletConnected ? 'text-green-400' : 'text-yellow-400')}
        />
        {walletConnected ? (
          <span className="text-green-300">
            Wallet connected:{' '}
            <span className="font-mono">
              {walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}
            </span>
          </span>
        ) : (
          <div className="flex items-center gap-2 flex-1">
            <span className="text-yellow-300">Wallet not connected</span>
            <button
              onClick={connectWallet}
              className="ml-auto text-[9px] px-1.5 py-0.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              Connect
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-1.5">
        {(['all', 'read', 'write'] as const).map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={cn(
              'px-2 py-1 text-[10px] rounded transition-colors capitalize',
              filterType === type
                ? type === 'read'
                  ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30'
                  : type === 'write'
                    ? 'bg-orange-600/20 text-orange-400 border border-orange-600/30'
                    : 'bg-[#3c3c3c] text-white border border-[#555]'
                : 'bg-[#2d2d2d] text-[#888]',
            )}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Search */}
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search functions..."
        className="w-full bg-[#2d2d2d] text-xs text-[#d4d4d4] rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 border border-[#444] placeholder-[#666]"
      />

      {/* Functions */}
      <div className="space-y-1.5">
        {filteredFunctions.length === 0 ? (
          <p className="text-[10px] text-[#555] italic text-center py-4">No matching functions</p>
        ) : (
          filteredFunctions.map((fn) => (
            <FunctionCard
              key={fn.name}
              fn={fn}
              contractAddress={contractAddress}
              rpcUrl="" // Will use workspace RPC or default
              abi={(compileResult.abi ?? []) as AbiFunction[]}
              walletConnected={walletConnected}
            />
          ))
        )}
      </div>
    </div>
  );
}
